/**
 * The C64's story screen before level 1 (spec 08 §1), traced in the rebb64 disassembly:
 *
 * - `D_08E4` ($08E4, game start): `D_0885` clears the screen and scatters 200 star characters
 *   ($FE/$FF) at random over all 40x25 cells; tune 1 starts (the intro, running on into the main
 *   theme); the text at $AC41 is drawn by `display_text_string` ($E42A); Bub and Bob are put at
 *   X $74 / $E4, Y $83 (P2 even in a one-player game: `inc $B3` at $0904, undone at $097D) and
 *   `setup_player_sprites` ($E4DA) points all eight sprites at the dragon-in-a-bubble pictures
 *   ($7440, `data/bubble-dragon-in-bubble.tga`: each dragon in a big bubble, 2x2 sprites).
 * - $0913: the sprites are switched on, then `D_7BC8` waits A = $FF = 255 frames; then the screen
 *   is cleared and level 1 begins. So the screen holds 255 frames (5.1 s at 50 Hz), 257 counting
 *   the two single-frame waits before it ($0909, $E4DF).
 * - While it holds, the split IRQ ($072E, `ZP_20` = 1) swaps each dragon between its two pictures
 *   every 8 frames (arms and bubble shine move; P1 and P2 in opposite phase).
 * - Stars: character $FE is a one-row dot in $D022 (blue, 6), $FF a two-row dot in $D023 (cyan, 3),
 *   from `$1C`/`$1E` set at $08A5.
 *
 * The session draws the lines as HUD banners; `StoryView` is the scene behind them.
 */

import { type C64TextLine, decodeC64Text } from './c64Text';

/** The string at $AC41 (`D_AC41`, game-tables-2.s), byte for byte, through its $10 terminator. */
export const STORY_BYTES: readonly number[] = [
  0x1f, 0x05, 0x00, 0x07, 0x4e, 0x4f, 0x57, 0x5e, 0x40, 0x49, 0x54, 0x40, 0x49, 0x53, 0x40, 0x54, 0x48, 0x45,
  0x40, 0x42, 0x45, 0x47, 0x49, 0x4e, 0x4e, 0x49, 0x4e, 0x47, 0x40, 0x4f, 0x46, 0x40, 0x41, 0x1f, 0x05, 0x02,
  0x46, 0x41, 0x4e, 0x54, 0x41, 0x53, 0x49, 0x43, 0x40, 0x53, 0x54, 0x4f, 0x52, 0x59, 0x5c, 0x5c, 0x40, 0x4c,
  0x45, 0x54, 0x5f, 0x53, 0x40, 0x4d, 0x41, 0x4b, 0x45, 0x40, 0x41, 0x1f, 0x04, 0x04, 0x4a, 0x4f, 0x55, 0x52,
  0x4e, 0x45, 0x59, 0x40, 0x54, 0x4f, 0x40, 0x54, 0x48, 0x45, 0x40, 0x43, 0x41, 0x56, 0x45, 0x40, 0x4f, 0x46,
  0x40, 0x4d, 0x4f, 0x4e, 0x53, 0x54, 0x45, 0x52, 0x53, 0x5c, 0x1f, 0x0d, 0x06, 0x5c, 0x40, 0x47, 0x4f, 0x4f,
  0x44, 0x40, 0x40, 0x4c, 0x55, 0x43, 0x4b, 0x40, 0x5c, 0x10,
];

/**
 * The story's four lines as the C64 draws them: columns and rows of its 40x25 text screen, C64
 * colour 7 (yellow). Each line is centred on that screen (column 19.5). "FANTASIC" is the C64's own
 * spelling.
 */
export const STORY_LINES: readonly C64TextLine[] = decodeC64Text(STORY_BYTES);

/** Frames the C64 holds the story screen with the dragons shown (`D_7BC8` with A = $FF at $0918). */
export const STORY_FRAMES = 255;

/** The same in seconds (PAL, 50 frames a second). */
export const STORY_SECONDS = STORY_FRAMES / 50;

/** Frames each dragon-in-a-bubble picture is held before the IRQ swaps it (frame counter & 7). */
export const STORY_PICTURE_FRAMES = 8;

/**
 * Where the dragons' pictures sit on the C64's 320x200 screen, pixels from its top-left: each is
 * 48x42, P1 from X $74 - $0A - 24 = 82, P2 from $E4 - $0A - 24 = 194, both from Y $83 - 8 - 50 = 73
 * (`update_player_sprite_positions`, $E4DF).
 */
export const STORY_DRAGONS = { p1: { x: 82, y: 73 }, p2: { x: 194, y: 73 }, w: 48, h: 42 } as const;

/**
 * HUD y (640x360, y down) of a C64 text row's top: the 25 rows are the playfield's, 14 texels each,
 * starting 5 texels down. Lines centred on the C64's screen are centred on the HUD's (x = 320).
 */
export function storyRowY(row: number): number {
  return 5 + row * 14;
}
